import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { ArrowUpFromLine, Check, X, Search, Copy } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money, dateFmt, shortAddr } from "@/lib/format";

const FILTERS = ["all", "pending", "paid", "rejected"];

export default function AdminWithdrawals() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const load = () => api.get("/admin/withdrawals").then(({ data }) => setRows(data)).catch(() => toast.error("Load failed"));
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { all: rows.length, pending: 0, paid: 0, rejected: 0 };
    rows.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (!q) return true;
      return (r.user_email || "").toLowerCase().includes(q) || (r.to_address || "").toLowerCase().includes(q);
    });
  }, [rows, filter, query]);

  const act = async (id, action) => {
    try {
      const { data } = await api.post(`/admin/withdrawals/${id}/${action}`);
      toast.success(action === "pay" ? (data.txid ? `Paid on-chain: ${shortAddr(data.txid)}` : "Marked paid") : "Withdrawal rejected");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-withdrawals-page">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2"><ArrowUpFromLine className="w-6 h-6 text-amber-400" /> Withdrawals</h1>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex flex-wrap gap-2" data-testid="withdrawal-filters">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`text-xs px-3 py-2 rounded-lg font-semibold capitalize transition-all ${filter === f ? "gold-btn" : "text-slate-400 border border-amber-500/20 hover:text-amber-300"}`}>
              {f} <span className="opacity-70">({counts[f] ?? 0})</span>
            </button>
          ))}
        </div>
        <div className="relative sm:w-72">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="withdrawal-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search email or address..."
            className="w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-lg pl-9 pr-3 py-2 text-sm outline-none" />
        </div>
      </div>

      <div className="glass rounded-2xl p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
            <th className="text-left py-3">User</th><th className="text-left py-3">Amount</th><th className="text-left py-3">Net</th><th className="text-left py-3">To Address</th><th className="text-left py-3">Status</th><th className="text-left py-3">Date</th><th className="text-right py-3">Action</th>
          </tr></thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-slate-800/60" data-testid={`admin-withdrawal-${r.id}`}>
                <td className="py-3 text-slate-300">{r.user_email}</td>
                <td className="py-3 font-mono-fin gold-text font-bold">{money(r.amount)}</td>
                <td className="py-3 font-mono-fin text-emerald-400">{money(r.net)}</td>
                <td className="py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-fin text-xs text-sky-400" title={r.to_address}>{shortAddr(r.to_address)}</span>
                    <button data-testid={`copy-address-${r.id}`} title="Copy full address"
                      onClick={() => { navigator.clipboard.writeText(r.to_address); toast.success("Address copied"); }}
                      className="p-1 rounded hover:bg-amber-500/15 text-slate-400 hover:text-amber-300">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
                <td className="py-3"><span className={`text-xs px-2 py-1 rounded ${r.status === "paid" ? "bg-emerald-500/15 text-emerald-400" : r.status === "rejected" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>{r.status}</span></td>
                <td className="py-3 text-slate-500 text-xs">{dateFmt(r.created_at)}</td>
                <td className="py-3 text-right">
                  {r.status === "pending" && (
                    <div className="flex gap-2 justify-end">
                      <button data-testid={`pay-withdrawal-${r.id}`} onClick={() => act(r.id, "pay")} className="px-3 py-2 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 text-xs font-bold flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Pay</button>
                      <button data-testid={`reject-withdrawal-${r.id}`} onClick={() => act(r.id, "reject")} className="p-2 rounded-lg bg-red-500/15 text-red-400 hover:bg-red-500/25"><X className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500">No withdrawals match this filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
